import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, of, take } from 'rxjs';
import { MenuService } from './menu.service';

// Navigation state for user-initiated visits to the home screen (landing's menu
// button, bottom-bar home) — those should land on the home screen even when
// there's only one super-section, unlike automatic redirects.
export const SHOW_MENU_HOME_STATE = { showMenuHome: true };

// When the menu has exactly one super-section, there's nothing for the home
// screen to let you pick between — send redirects here (boot without a landing
// page, bounce-backs from menu-list, post-payment returns) straight to that
// section instead. Visits carrying SHOW_MENU_HOME_STATE are let through.
//
// On a direct/refreshed load of this route, the router resolves guards before
// the boot HTTP chain has populated the menu (nothing ties Angular's initial
// navigation to that fetch), so `menuService.menu()` starts out null. Waiting
// for it here — rather than reading `superSections()` synchronously — avoids
// deciding on an empty menu and getting stuck on the home screen regardless of
// how many super-sections actually exist.
export const menuHomeGuard: CanActivateFn = () => {
  const menuService = inject(MenuService);
  const router = inject(Router);

  if (router.currentNavigation()?.extras.state?.['showMenuHome']) {
    return of(true);
  }

  return toObservable(menuService.menu).pipe(
    filter(menu => menu !== null),
    take(1),
    map(() => {
      const superSections = menuService.superSections();
      return superSections.length === 1
        ? router.parseUrl(`/menu-list/${superSections[0].id}`)
        : true;
    }),
  );
};
